import type { LocationType } from '@cuito/types';
import type { LocationView } from '../api/stock';

/** 地点列表的类型筛选（`05-ui-design-system.md` §3.2：门店 / 展会 / 仓库都在同一页可见） */
export type LocationFilter = 'ALL' | LocationType;

/** 显示词取自 §7.2 术语表：仓库 / 门店 / 展会 / 寄售暂存 */
export const LOCATION_FILTERS: ReadonlyArray<{ value: LocationFilter; label: string }> = [
  { value: 'ALL', label: '全部' },
  { value: 'SHOP', label: '门店' },
  { value: 'EVENT', label: '展会' },
  { value: 'WAREHOUSE', label: '仓库' },
  { value: 'CONSIGN_HOLD', label: '寄售暂存' },
];

export function filterLocations(
  locations: readonly LocationView[],
  filter: LocationFilter,
): LocationView[] {
  return filter === 'ALL' ? [...locations] : locations.filter((location) => location.type === filter);
}

/**
 * 该类型的地点能否建货架。
 *
 * 权威判定在服务端（422 `LOCATION_NOT_SHELVABLE`，契约 §1.4 `:162`）；
 * 这里只用来**不显示**注定失败的入口，不是门禁。
 */
export function isShelvable(type: LocationType): boolean {
  return type === 'SHOP' || type === 'EVENT';
}

/** 入口链接的展示文本：拿不到就给占位符，别让用户复制到空串或 `undefined` */
export function entryUrlText(entryUrl: string | null | undefined): string {
  return typeof entryUrl === 'string' && entryUrl !== '' ? entryUrl : '-';
}

/**
 * 地点码图像的取值：**服务端图优先，前端现渲染兜底**。
 *
 * 优先用服务端渲染并落盘的那张（契约 §6.1 的 `qrImageUrl`，形如
 * `/api/files/location_codes/<uuid>.png`）：**已含全局 `/api` 前缀**（契约 §1.1），
 * 前端原样使用，既不加也不去前缀；同一份内容由同一处生成，尺寸与纠错级别
 * 不会与前端实现漂移，打印/导出也走它。
 *
 * 服务端还没给出图时（该端点在 Task 6 之前不存在，或响应里就是 `null`）回退到
 * 前端按 `entryUrl` 现渲染的 data URL：宁可自己渲染一张，也不要给用户一张破图
 * 或一段空白。两条路都没有（载荷为空等）才返回 `null`，由调用方显示占位。
 */
export function qrImageSource(
  qrImageUrl: string | null | undefined,
  fallbackDataUrl: string | null,
): string | null {
  if (typeof qrImageUrl === 'string' && qrImageUrl !== '') return qrImageUrl;
  return fallbackDataUrl;
}

/**
 * 门店列表里「地点码」展开态的单开归约：点已展开的那一行收起，点另一行切过去。
 *
 * **只允许一行展开**（不做各自独立的开关）：面板一展开就会去请求地点码、并在缺图时
 * 现渲染一张二维码，多行同时展开会并发发起这些开销；更重要的是同屏多张几乎一样的
 * 二维码，反而让人分不清自己正在看哪家店——单开让「当前正在看谁」永远只有一个答案。
 * 代价只是收起上一行后要重新拉一次（`EntryCodePanel` 不缓存），换来的确定性强得多。
 *
 * 收成纯函数是因为这里的**判定顺序是容易写错的点**：正确的语义必须先把
 * `clickedShopId === expandedShopId` 判成收起，再谈切换——若反过来写成
 * 「点任意行都设成该行」，展开后的行就再也收不起来了。这种「点同一行应收起、
 * 点另一行应切换」正是单测能钉死、而组件测试在本包又跑不了的部分。
 */
export function toggledShopId(
  expandedShopId: string | null,
  clickedShopId: string,
): string | null {
  return expandedShopId === clickedShopId ? null : clickedShopId;
}
